// apps/web/src/pages/AdminMemberDetail.tsx
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  saveMemberAdmin,
  useMember,
  type AdminMemberContact,
  type MemberContact,
} from '../lib/members';
import { useAllBoatPlaces, useBoatsForOwner } from '../lib/boats';
import MemberForm from '../components/MemberForm';
import DeleteMemberDialog from '../components/DeleteMemberDialog';
import ApprovalBadge from '../components/ApprovalBadge';
import { callDeleteMember } from '../lib/admin';

export default function AdminMemberDetail() {
  const { uid = '' } = useParams();
  const navigate = useNavigate();
  const { member, loading } = useMember(uid);
  const { places } = useAllBoatPlaces();
  const { boats } = useBoatsForOwner(uid);
  const [showDelete, setShowDelete] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (loading) return <p>Laddar…</p>;
  if (!member) return <p>Medlem hittades inte.</p>;

  const memberPlaces = places.filter((p) => p.currentOwnerUid === uid);

  const initialContact: MemberContact = {
    firstName: member.firstName,
    lastName: member.lastName,
    address: member.address,
    postalCode: member.postalCode,
    city: member.city,
    alternateAddress: member.alternateAddress,
    phoneLandline: member.phoneLandline,
    phoneMobile: member.phoneMobile,
    spouse: member.spouse,
  };

  async function handleSubmit(values: AdminMemberContact) {
    setError(null);
    try {
      await saveMemberAdmin(uid, values);
    } catch (e) {
      setError((e as Error).message);
    }
  }

  async function handleDelete(deleteBoats: boolean) {
    setShowDelete(false);
    setError(null);
    try {
      await callDeleteMember(uid, deleteBoats);
      navigate('/admin/medlemmar');
    } catch (e) {
      setError((e as Error).message);
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between">
        <Link to="/admin/medlemmar" className="text-sm underline">
          ← Tillbaka
        </Link>
        <button
          type="button"
          onClick={() => setShowDelete(true)}
          className="px-3 py-1 text-sm bg-red-700 text-white rounded"
        >
          Radera permanent
        </button>
      </div>
      <h1 className="text-xl font-bold">
        {member.firstName} {member.lastName}
      </h1>
      {error && <p className="text-sm text-red-700">{error}</p>}

      <section>
        <h2 className="font-bold mb-2">Medlem</h2>
        <MemberForm
          initial={initialContact}
          email={member.email}
          status={member.status}
          mode="admin"
          onSave={handleSubmit}
        />
      </section>

      <section>
        <h2 className="font-bold mb-2">Båtplatser ({memberPlaces.length})</h2>
        <ul className="list-disc ml-5 text-sm">
          {memberPlaces.length === 0 && <li>(inga)</li>}
          {memberPlaces.map((p) => (
            <li key={p.id}>
              <Link
                to={`/admin/batplatser/${p.id}`}
                className="text-blue-700 underline"
              >
                Plats {p.number}
              </Link>
            </li>
          ))}
        </ul>
      </section>

      <section>
        <h2 className="font-bold mb-2">Båtar ({boats.length})</h2>
        <ul className="space-y-1 text-sm">
          {boats.length === 0 && <li>(inga)</li>}
          {boats.map((b) => (
            <li key={b.id} className="flex items-center gap-2">
              <Link
                to={`/admin/medlemmar/${uid}/batar/${b.id}`}
                className="text-blue-700 underline"
              >
                {b.name ?? '(namnlös)'} {b.model && `— ${b.model}`}
              </Link>
              <ApprovalBadge approval={b.approval} />
            </li>
          ))}
        </ul>
      </section>

      {showDelete && (
        <DeleteMemberDialog
          memberName={`${member.firstName} ${member.lastName}`}
          placeCount={memberPlaces.length}
          boatCount={boats.length}
          onConfirm={handleDelete}
          onCancel={() => setShowDelete(false)}
        />
      )}
    </div>
  );
}
